import {
  MembershipStatusEnum,
  PermissionEnum,
  Workspace,
} from '@sr-sdks/api-sdk-axios';
import { Alert, Dialog } from '@sr/ui-library';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getMsr2Base } from '../utils/embedAuth';
import { isRoyaltiesOnlyWorkspace } from '../utils/helpers/common';
import ProtectElement from './ProtectElement';

interface AppChooserProps {
  workspace: Workspace | undefined;
}

export const AppChooser = ({ workspace }: AppChooserProps) => {
  const [state, setState] = useState({
    canSwitchApp: false,
    isRoyaltiesOnly: false,
    mustSwitchApp: false,
    showAwaitingUpdateDialog: false,
    showDisclaimerDialog: false,
    showInReviewDialog: false,
    showInactiveDialog: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const myScreenrightsUrl = getMsr2Base();

  useEffect(() => {
    if (!workspace) {
      return;
    }

    const isRoyaltiesOnly = isRoyaltiesOnlyWorkspace(workspace);

    const canSwitch =
      workspace.membershipStatus === MembershipStatusEnum.CurrentMember;

    const mustSwitch = canSwitch && isRoyaltiesOnly;

    setState({
      canSwitchApp: canSwitch,
      isRoyaltiesOnly: isRoyaltiesOnly,
      mustSwitchApp: mustSwitch,
      showAwaitingUpdateDialog:
        workspace.membershipStatus === MembershipStatusEnum.AwaitingUpdate &&
        !location.pathname.includes('manage-workspace'),
      showDisclaimerDialog: mustSwitch,
      showInReviewDialog:
        isRoyaltiesOnly &&
        [
          MembershipStatusEnum.InReview,
          MembershipStatusEnum.PendingApproval,
        ].includes(workspace.membershipStatus as any),
      showInactiveDialog:
        isRoyaltiesOnly &&
        [
          MembershipStatusEnum.InactiveMember,
          MembershipStatusEnum.NonMember,
          MembershipStatusEnum.Rejected,
          MembershipStatusEnum.RemovedMember,
          MembershipStatusEnum.ResignedMember,
        ].includes(workspace.membershipStatus as any),
    });
  }, [workspace, location.pathname]);

  const handleCancelClick = () => {
    if (state.isRoyaltiesOnly) {
      return navigate('/dashboard');
    }

    // User chooses to stay in the app, allow them to continue
    setState({
      ...state,
      showAwaitingUpdateDialog: false,
      showDisclaimerDialog: false,
    });
  };

  return (
    <>
      {state.canSwitchApp && (
        <>
          {/* Wrapper width matches DefaultLayoutBody; uncapped, this alert
              outgrew the page content on wide screens. */}
          <div className="px-5 pt-8 xl:mx-auto xl:max-w-screen-4xl">
            <Alert
              actionButtonProps={{
                children: 'Switch to MyScreenrights',
                onClick: () =>
                  setState({ ...state, showDisclaimerDialog: true }),
              }}
              hasActionButton
              variant="info"
            >
              This workspace is configured for royalties. You can switch to
              MyScreenrights to access your title registrations, competing
              claims, and royalty payment statements.
            </Alert>
          </div>

          <Dialog
            heading="Switching to MyScreenrights"
            actionButtonProps={{
              asChild: true,
              children: (
                <Link
                  rel="noopener noreferrer"
                  target="_blank"
                  to={`${myScreenrightsUrl}/v2/?workspaceId=${workspace?._id}`}
                >
                  Proceed to MyScreenrights
                </Link>
              ),
            }}
            cancelButtonProps={{
              children: 'Go back',
              onClick: handleCancelClick,
              variant: 'outline',
            }}
            hasCloseButton={!state.mustSwitchApp}
            open={state.showDisclaimerDialog}
            onOpenChange={(open) => {
              if (!open && !state.mustSwitchApp) handleCancelClick();
            }}
          >
            <div className="flex flex-col gap-4">
              <p>
                {state.isRoyaltiesOnly && (
                  <span>This workspace is configured for royalties only. </span>
                )}
                You are about to switch to MyScreenrights to access title
                registrations, competing claims, and royalty payment statements.
              </p>
              <p className="text-sr-gray text-sm">
                The design may look slightly different as we work to combine
                both apps into a single, seamless experience.
              </p>
            </div>
          </Dialog>
        </>
      )}

      {state.showAwaitingUpdateDialog && (
        <ProtectElement
          permissionRequired={[PermissionEnum.EditWorkspaceDetails]}
        >
          <Dialog
            heading="Membership Awaiting Update"
            actionButtonProps={{
              asChild: true,
              children: (
                <Link
                  to={`/workspace/${workspace?._id}/manage-workspace/details`}
                >
                  Proceed to Settings
                </Link>
              ),
            }}
            cancelButtonProps={{
              children: state.isRoyaltiesOnly ? 'Go back' : 'Close',
              onClick: handleCancelClick,
              variant: 'outline',
            }}
            hasCloseButton={false}
            open={state.showAwaitingUpdateDialog}
          >
            <div className="flex flex-col gap-4">
              <p>
                Your membership application for{' '}
                <strong>{workspace?.workspaceName}</strong> requires additional
                information.
              </p>
            </div>
          </Dialog>
        </ProtectElement>
      )}

      {state.showInReviewDialog && (
        <Dialog
          heading="Membership In Review"
          cancelButtonProps={{
            children: 'Go to Dashboard',
            onClick: handleCancelClick,
            variant: 'outline',
          }}
          hasActionButton={false}
          hasCloseButton={false}
          open={state.showInReviewDialog}
        >
          <div className="flex flex-col gap-4">
            <p>
              Our team is reviewing your workspace details and Membership
              Agreement for compliance (typical turnaround time: 1-2 business
              days).
            </p>
            <p>
              Once approved, you will receive a confirmation email with
              instructions on how to register your titles.
            </p>
          </div>
        </Dialog>
      )}

      {state.showInactiveDialog && (
        <>
          <Dialog
            heading="Membership Inactive"
            cancelButtonProps={{
              children: 'Go to Dashboard',
              onClick: handleCancelClick,
              variant: 'outline',
            }}
            hasActionButton={false}
            hasCloseButton={false}
            open={state.showInactiveDialog}
          >
            <div className="flex flex-col gap-4">
              <p>
                If <strong>{workspace?.workspaceName}</strong> needs to collect
                secondary royalties or access other Screenrights industry
                services, please contact{' '}
                <a
                  className="font-semibold underline"
                  href="mailto:support@screenrights.org"
                >
                  support@screenrights.org
                </a>
                .
              </p>
            </div>
          </Dialog>
        </>
      )}
    </>
  );
};
